import { mergeDeepRight } from 'ramda';
import {
    type ComponentRef,
    type MouseEventHandler,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import type { DeepPartial } from 'utility-types';
import '../../../../css/tenant.less';
import { TAB_TYPES } from '../../constants/tab';
import type { TENANT_VIEWS } from '../../constants/tenant';
import { useRouting } from '../../hooks/route';
import { useScrollSpy } from '../../hooks/scroll';
import { getCurrentTenant, updateCurrentTenant } from '../../sources/tenant';
import translations from '../../translations';
import type { IPRangeAPI, TenantRequestAPI, TenantResponseAPI } from '../../types/Tenant';
import { useAuth } from '../AuthProvider';
import { useFeatureFlag } from '../featureFlags/FeatureFlagProvider';
import FormGroup from '../generic/FormGroup';
import Toggle from '../inputs/Toggle';
import Loader from '../loader/Loader';
import { useNotificationDispatch } from '../notifications/NotificationProvider';
import { useTabs } from '../tabs/TabsProvider';
import DeveloperName from './DeveloperName';
import ExportTenant from './ExportTenant';
import ImportTenant from './ImportTenant';
import IntegrationAccounts from './IntegrationAccounts';
import OtlpSettings from './OtlpSettings';
import Reporting from './Reporting';
import Security from './Security';
import SubtenantList from './SubtenantList';
import UserList from './UserList';
import SAMLSettings from './saml/SAMLSettings';
import { useTenant } from '../TenantProvider';

export type UpdateTenant = (updatedTenant: DeepPartial<TenantRequestAPI>) => void;

type TenantProps = {
    navigateTo: (view: keyof typeof TENANT_VIEWS) => void;
    setActiveSectionId: (id: string | null) => void;
    hasMenu: boolean;
};

const TenantPage = ({ hasMenu, navigateTo, setActiveSectionId }: TenantProps) => {
    const { user, fetchUser } = useAuth();
    const authorizedTenant = useTenant();

    const dispatchNotification = useNotificationDispatch();

    const contentRef = useRef<HTMLDivElement>(null);

    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [isConfigValid, setIsConfigValid] = useState(true);
    const [currentTenant, setCurrentTenant] = useState<TenantResponseAPI | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { closeTab } = useRouting();
    const { tabs } = useTabs();

    // using a scrollspy based on the intersection observer to report back to
    // the side-menu the currently active section so the side-menu can update
    // itself (every content page with a side-menu will have to implement this
    // hook)
    useScrollSpy(contentRef, hasMenu, setActiveSectionId);

    const { getFlag } = useFeatureFlag();

    useEffect(() => {
        const fetchCurrentTenant = async () => {
            try {
                setIsLoading(true);

                // Only users with the administrator role can access the tenant API endpoint
                const fetchedTenant = await getCurrentTenant();

                setCurrentTenant(fetchedTenant);
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: { error },
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchCurrentTenant();
    }, [dispatchNotification]);

    const updateTenant: UpdateTenant = useCallback((updatedTenant) => {
        setCurrentTenant((previousTenant) => {
            if (!previousTenant?.id) {
                return previousTenant;
            }

            const mergedTenant = mergeDeepRight(previousTenant, updatedTenant) as TenantResponseAPI;

            return mergedTenant;
        });
    }, []);

    const handleSave: MouseEventHandler<ComponentRef<'button'>> = async () => {
        setHasSubmitted(true);

        if (!isConfigValid || !currentTenant?.id) {
            return;
        }

        const validateIpRanges = () => {
            const {
                authorizedAdminIPRanges,
                authorizedDrawIPRanges,
                authorizedPackagingIPRanges,
                authorizedRunIPRanges,
                isAdminRestrictedByIPRange,
                isDrawRestrictedByIPRange,
                isPackagingRestrictedByIPRange,
                isRunRestrictedByIPRange,
            } = currentTenant.securitySettings ?? {};

            // Include only enabled ranges in validation
            const ipRanges: IPRangeAPI[] = [
                ...(isAdminRestrictedByIPRange ? (authorizedAdminIPRanges ?? []) : []),
                ...(isDrawRestrictedByIPRange ? (authorizedDrawIPRanges ?? []) : []),
                ...(isPackagingRestrictedByIPRange ? (authorizedPackagingIPRanges ?? []) : []),
                ...(isRunRestrictedByIPRange ? (authorizedRunIPRanges ?? []) : []),
            ];

            const areIpRangesValid = ipRanges.every((range) => {
                return range?.developerName && range?.startIPAddress && range.endIPAddress;
            });

            return areIpRangesValid;
        };

        if (!validateIpRanges()) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error: translations.TENANT_security_ip_setting_invalid_range_error_message,
                },
            });
            return;
        }

        try {
            setIsLoading(true);

            const tenant = await updateCurrentTenant(currentTenant);

            setCurrentTenant(tenant);

            await fetchUser();
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: { error },
            });
        } finally {
            setIsLoading(false);
        }

        for (const { key, type } of tabs) {
            if (
                !currentTenant.tenantSettings?.environments &&
                (type === TAB_TYPES.environment || type === TAB_TYPES.environments)
            ) {
                closeTab(key);
            }

            if (
                type ===
                (currentTenant.tenantSettings?.themes ? TAB_TYPES.players : TAB_TYPES.themes)
            ) {
                closeTab(key);
            }
        }
    };

    return (
        <>
            <div className="admin-page tenant-page" ref={contentRef}>
                {!(currentTenant?.id && user?.id) || isLoading ? (
                    <Loader />
                ) : (
                    <>
                        <section data-testid="tenant-name-section">
                            <h2 id="tenant-name">{translations.TENANT_tenant_name_heading_text}</h2>
                            <DeveloperName
                                tenantName={currentTenant.developerName ?? ''}
                                parentTenantName={currentTenant.parentTenant?.developerName ?? ''}
                                showValidation={hasSubmitted}
                                onChange={(name) => {
                                    updateTenant({
                                        developerName: name,
                                    });
                                }}
                                onValidate={setIsConfigValid}
                            />
                        </section>
                        <section data-testid="tenant-users-section">
                            <h2 id="tenant-users">
                                {translations.TENANT_tenant_users_heading_text}
                            </h2>
                            <p>{translations.TENANT_user_description}</p>
                            <UserList userId={user.id} />
                        </section>
                        <section data-testid="tenant-environment-section">
                            <h2 id="tenant-environment">{translations.ENVIRONMENT_name}</h2>
                            <p>{translations.ENVIRONMENT_description}</p>
                            <FormGroup
                                htmlFor="environments-setting"
                                label={translations.ENVIRONMENT_toggle_message}
                                labelPosition="after"
                            >
                                <Toggle
                                    id="environments-setting"
                                    isOn={currentTenant.tenantSettings?.environments ?? false}
                                    onChange={({ isOn }) => {
                                        updateTenant({
                                            tenantSettings: {
                                                environments: isOn,
                                            },
                                        });
                                    }}
                                    testId="environment-setting"
                                    isDisabled={
                                        (currentTenant?.hasMcr && !getFlag('MCRENV')) ?? false
                                    }
                                />
                            </FormGroup>
                            {currentTenant?.hasMcr && !getFlag('MCRENV') ? (
                                <p data-testid="environment-toggle-disabled-message">
                                    {translations.ENVIRONMENT_toggle_disabled}
                                </p>
                            ) : null}
                        </section>
                        <section data-test="tenant-themes-section">
                            <h2 id="tenant-themes">{translations.TENANT_theme_heading}</h2>
                            <p>{translations.TENANT_theme_description}</p>
                            <FormGroup
                                htmlFor="theme-setting"
                                label={translations.TENANT_toggle_message}
                                labelPosition="after"
                            >
                                <Toggle
                                    id="theme-setting"
                                    isOn={currentTenant.tenantSettings?.themes ?? true}
                                    onChange={({ isOn }) => {
                                        updateTenant({
                                            tenantSettings: {
                                                themes: isOn,
                                            },
                                        });
                                    }}
                                    testId="theme-setting"
                                />
                            </FormGroup>
                        </section>
                        <section data-testid="tenant-integration-accounts-section">
                            <h2 id="tenant-integration-accounts">
                                {translations.TENANT_integration_accounts}
                            </h2>
                            <IntegrationAccounts
                                integrationAccounts={
                                    currentTenant.tenantSettings?.integrationAccounts ?? []
                                }
                                updateTenant={updateTenant}
                            />
                        </section>
                        {!currentTenant.parentTenant && (
                            <section data-testid="tenant-subtenants-section">
                                <h2 id="tenant-subtenants">
                                    {translations.TENANT_subtenants_heading_text}
                                </h2>
                                <p>
                                    {translations.TENANT_subtenant_description_1}
                                    <br />
                                    {translations.TENANT_subtenant_description_2}
                                </p>
                                <SubtenantList navigateTo={navigateTo} />
                            </section>
                        )}
                        <section data-testid="tenant-single-sign-on-section">
                            <h2 id="tenant-single-sign-on">
                                {translations.TENANT_sso_heading_text}
                            </h2>
                            <p>{translations.TENANT_sso_description}</p>
                            <SAMLSettings
                                tenantId={currentTenant.id}
                                samlSettings={currentTenant.securitySettings?.samlSettings}
                                isSso={!!authorizedTenant?.isSso}
                                isSamlEnabled={!!currentTenant.securitySettings?.isSamlEnabled}
                                updateTenant={updateTenant}
                            />
                        </section>
                        <section data-testid="tenant-security-section">
                            <h2 id="tenant-security">
                                {translations.TENANT_security_heading_text}
                            </h2>
                            <p>{translations.TENANT_security_description}</p>
                            <Security
                                securitySettings={currentTenant.securitySettings}
                                updateTenant={updateTenant}
                            />
                        </section>
                        <section data-testid="tenant-reporting-section">
                            <h2 id="tenant-reporting">
                                {translations.TENANT_reporting_heading_text}
                            </h2>
                            <p>{translations.TENANT_reporting_description}</p>
                            <Reporting
                                endpoint={currentTenant.stateSettings?.endpoint}
                                disableServiceInvokerLogging={
                                    currentTenant.tenantSettings?.disableServiceInvokerLogging
                                }
                                updateTenant={updateTenant}
                            />
                        </section>
                        <section data-testid="tenant-observability-section">
                            <h2 id="tenant-observability">Observability</h2>
                            <OtlpSettings
                                observabilitySettings={currentTenant.observabilitySettings}
                                updateTenant={updateTenant}
                            />
                        </section>
                        <section data-testid="tenant-export-section">
                            <h2 id="tenant-export">Export</h2>
                            <ExportTenant tenantName={currentTenant.developerName ?? ''} />
                        </section>
                        <section data-testid="tenant-import-section">
                            <h2 id="tenant-import">Import</h2>
                            <ImportTenant tenantName={currentTenant.developerName ?? ''} />
                        </section>
                        <section data-testid="tenant-comments-section">
                            <h2 id="tenant-comments">
                                {translations.TENANT_tenant_comments_heading_text}
                            </h2>
                            <FormGroup
                                htmlFor="developer-summary"
                                label={translations.TENANT_developer_summary_label}
                            >
                                <textarea
                                    id="developer-summary"
                                    maxLength={1000}
                                    rows={3}
                                    className="form-control tenant-comments"
                                    value={currentTenant.developerSummary ?? ''}
                                    onChange={({ target: { value } }) => {
                                        updateTenant({
                                            developerSummary: value,
                                        });
                                    }}
                                />
                            </FormGroup>
                        </section>
                    </>
                )}
            </div>
            <div className="admin-footer">
                <button
                    type="button"
                    className="btn btn-primary outcome tenant-save"
                    onClick={handleSave}
                >
                    {translations.TENANT_save_settings_button_label}
                </button>
            </div>
        </>
    );
};

export default TenantPage;
